/**
 * Dashboard Tab for Admin Panel
 *
 * Shows platform overview: usage stats, cost breakdown,
 * 30-day activity chart, and recent errors.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  getDashboardStats,
  getAuditLogs,
  type DashboardStats,
  type AuditLogsResponse,
} from '@/services/adminApi';
import type { AuditLog } from '@/types/user';

interface DashboardTabProps {
  onTabChange: (tab: string) => void;
}

export default function DashboardTab({ onTabChange }: DashboardTabProps) {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [errors, setErrors] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [dashStats, auditRes] = await Promise.all([
        getDashboardStats(),
        getAuditLogs({ level: 'ERROR', limit: 10 }).catch((): AuditLogsResponse => ({
          logs: [],
          total: 0,
          limit: 10,
          offset: 0,
        })),
      ]);
      setStats(dashStats);
      setErrors(auditRes.logs);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCost = (val: number) => `$${val.toFixed(2)}`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error || 'Failed to load dashboard'}
        <button onClick={loadData} className="ml-4 underline">
          Retry
        </button>
      </div>
    );
  }

  const chartData = stats.activity_chart.map((d) => ({
    ...d,
    dayLabel: formatDate(d.day),
  }));

  return (
    <div className="space-y-6">
      {/* Row 1: Usage Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          accent="border-blue-500"
          icon={
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
          label="Users"
          value={stats.users.active.toLocaleString()}
          sub={`+${stats.users.new_this_month} this month`}
        />
        <StatCard
          accent="border-green-500"
          icon={
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          label="Active"
          value={stats.users.active_today.toLocaleString()}
          sub={`${stats.users.active_week} this week`}
        />
        <StatCard
          accent="border-purple-500"
          icon={
            <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
          label="Tasks"
          value={stats.tasks.total.toLocaleString()}
          sub={`${stats.tasks.completed} completed`}
        />
        <StatCard
          accent="border-amber-500"
          icon={
            <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          }
          label="Files"
          value={stats.files.total.toLocaleString()}
          sub={stats.processing.failed > 0 ? `${stats.processing.failed} failed` : `${stats.processing.ready} processed`}
        />
      </div>

      {/* Row 2: Cost Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 border-l-4 border-l-red-500">
          <p className="text-sm text-gray-500 mb-1">AI Tasks (30d)</p>
          <p className="text-2xl font-bold text-gray-900">{formatCost(stats.costs.ai_cost_30d)}</p>
          <p className="text-xs text-gray-400 mt-1">{formatCost(stats.costs.total_ai_cost)} all time</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 border-l-4 border-l-orange-500">
          <p className="text-sm text-gray-500 mb-1">File Processing</p>
          <p className="text-2xl font-bold text-gray-900">{formatCost(stats.costs.total_processing_cost)}</p>
          <p className="text-xs text-gray-400 mt-1">Total processing cost</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 border-l-4 border-l-green-500">
          <p className="text-sm text-gray-500 mb-1">Today's Spend</p>
          <p className={`text-2xl font-bold ${stats.costs.ai_cost_today > 1 ? 'text-red-600' : 'text-green-600'}`}>
            {formatCost(stats.costs.ai_cost_today)}
          </p>
          <p className="text-xs text-gray-400 mt-1">{stats.iterations.completed} iterations total</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500 mb-2">Top Spenders</p>
          <div className="space-y-1">
            {stats.costs.top_spenders.slice(0, 5).map((u, i) => (
              <button
                key={u.id}
                onClick={() => navigate(`/admin/users/${u.id}/stats`)}
                className="flex items-center justify-between w-full text-xs hover:bg-gray-50 rounded px-1 py-0.5 transition-colors"
              >
                <span className="text-gray-600 truncate">
                  <span className="text-gray-400 mr-1">{i + 1}.</span>
                  {u.full_name || u.username}
                </span>
                <span className="text-gray-900 font-medium ml-2">{formatCost(u.total_cost)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Activity Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Pulse &mdash; 30 Day Activity</h2>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="dayLabel"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                labelFormatter={(label) => `${label}`}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="logins"
                stackId="1"
                fill="#93c5fd"
                stroke="#3b82f6"
                fillOpacity={0.6}
                name="Logins"
              />
              <Area
                type="monotone"
                dataKey="file_ops"
                stackId="1"
                fill="#fcd34d"
                stroke="#f59e0b"
                fillOpacity={0.6}
                name="File Ops"
              />
              <Area
                type="monotone"
                dataKey="task_ops"
                stackId="1"
                fill="#c4b5fd"
                stroke="#8b5cf6"
                fillOpacity={0.6}
                name="Task Ops"
              />
              <Area
                type="monotone"
                dataKey="voice_sessions"
                stackId="1"
                fill="#6ee7b7"
                stroke="#10b981"
                fillOpacity={0.6}
                name="Voice"
              />
              <Line
                type="monotone"
                dataKey="unique_users"
                stroke="#1f2937"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Unique Users"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Errors */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Errors</h2>
          <button
            onClick={() => onTabChange('audit')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View All
          </button>
        </div>
        {errors.length === 0 ? (
          <p className="text-gray-500 text-sm">No recent errors. All systems running smoothly.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 pr-4">Time</th>
                  <th className="pb-2 pr-4">Level</th>
                  <th className="pb-2 pr-4">Endpoint</th>
                  <th className="pb-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((log) => (
                  <tr
                    key={log.id}
                    className={`border-b last:border-0 ${
                      log.level === 'CRITICAL' ? 'bg-red-50' : 'bg-yellow-50/50'
                    }`}
                  >
                    <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                      {formatTime(log.timestamp)}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.level === 'CRITICAL'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {log.level}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-600 font-mono text-xs truncate max-w-[200px]">
                      {log.endpoint || log.event || '-'}
                    </td>
                    <td className="py-2 text-gray-700 truncate max-w-[400px]">
                      {log.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card sub-component
function StatCard({
  accent,
  icon,
  label,
  value,
  sub,
}: {
  accent: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 border-l-4 ${accent}`}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <p className="text-sm text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 mt-1">{sub}</p>
    </div>
  );
}
